

import React, { useState } from "react";
import {
  Box, Grid, Card, Typography, TextField, Button,
  Checkbox, FormControlLabel, IconButton, InputAdornment,
  CircularProgress
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ayujalogo from "../../Logos/AuthScreens/AuthLogo.png";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Footer from "../../Common/Footer";
import { fetchUserFromRedis } from "./Register";
import CustomDialog from "../../../CustomDialog";

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ identifier: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);

  const loginAPI = process.env.REACT_APP_LOGIN_API;

  //  Dialog state for all messages
  const [dialog, setDialog] = useState({
    open: false,
    title: "",
    message: "",
    type: "info", // "success", "error", "warning", "info"
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleGoogleLogin = () => {
    window.location.href = process.env.REACT_APP_GOOGLE_REDIRECT_API;
  };

  const handleLogin = async () => {
    setLoading(true);
    try {
      const res = await axios.post(loginAPI, formData);
      console.log("Login response:", res.data);

      const userId = res.data.user.user_id;
      const token = res.data.token;
      const userRole = res.data.user.role;
      const approvalStatus = res.data.user.approval_status;
      const isApproved = res.data.user.is_approved;

      console.log("Approval Status:", approvalStatus);

      //  Check if user is pending approval (should be caught by backend but double-check)
      if (approvalStatus === "pending" || !isApproved) {
        setDialog({
          open: true,
          title: "Account Pending Approval",
          message: "Your account is awaiting admin approval. You will be notified once approved.",
          type: "warning",
        });
        setLoading(false);
        return;
      }

      //  Check if user was rejected (should be caught by backend but double-check)
      if (approvalStatus === "rejected") {
        setDialog({
          open: true,
          title: "Account Not Approved",
          message: "Your registration was Rejected. Please contact support for more information.",
          type: "error",
        });
        setLoading(false);
        return;
      }

      //  Only approved users proceed
      if (approvalStatus !== "approved") {
        setDialog({
          open: true,
          title: "Account Access Denied",
          message: "Your account status is invalid. Please contact support.",
          type: "error",
        });
        setLoading(false);
        return;
      }

      // Save token and ID
      sessionStorage.setItem("token", token);
      sessionStorage.setItem("userId", userId);

      // Block admins from resident app
      if (userRole === "admin" || userRole === "superadmin") {
        setDialog({
          open: true,
          title: "Access Denied",
          message: "Admins cannot use this resident app.",
          type: "error",
        });
        setLoading(false);
        return;
      }

      //  Fetch user data from Redis (optional)
      try {
        const userData = await fetchUserFromRedis(userId, token);
        setUser(userData);
      } catch (redisErr) {
        console.warn("Could not fetch user from Redis:", redisErr);
      }

      // Show success message
      setDialog({
        open: true,
        title: "Login Successful",
        message: "Welcome back!",
        type: "success",
      });

      //  Navigate after a brief delay to show success message
      setTimeout(() => {
        if (userRole === "resident") {
          navigate("/services");
        } else {
          navigate("/");
        }
      }, 1500);

    } catch (err) {
      console.error("Login error:", err);
      
      //  Handle 403 responses (approval status issues)
      if (err.response?.status === 403) {
        const errorData = err.response.data;
        const status = errorData.status;

        if (status === "pending_approval") {
          setDialog({
            open: true,
            title: "Account Pending Approval",
            message: errorData.error || "Your account is awaiting admin approval.",
            type: "warning",
          });
        } else if (status === "rejected") {
          setDialog({
            open: true,
            title: "Account Not Approved",
            message: errorData.error || "Your registration was not approved.",
            type: "error",
          });
        } else {
          setDialog({
            open: true,
            title: "Access Denied",
            message: errorData.error || "You do not have permission to access this application.",
            type: "error",
          });
        }
      } 
      //  Handle 401 (invalid credentials)
      else if (err.response?.status === 401) {
        setDialog({
          open: true,
          title: "Login Failed",
          message: "Invalid email/phone or password. Please try again.",
          type: "error",
        });
      }
      //  Handle 404 (user not found)
      else if (err.response?.status === 404) {
        setDialog({
          open: true,
          title: "User Not Found",
          message: "No account found with this email or phone number.",
          type: "error",
        });
      }
      //  Handle all other errors
      else {
        setDialog({
          open: true,
          title: "Login Failed",
          message: err.response?.data?.message || "An error occurred. Please try again.",
          type: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
              <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "80vh",
              p: { xs: 1, sm: 1, md: 1 },
              backgroundColor: "#fff"
            }}
          >
            <Card 
              sx={{ 
                borderRadius: "16px", 
                maxWidth: "1000px",
                width: "100%",
                overflow: "hidden",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)"
              }}
            >
          <Grid container>
            
            {/* Left Section */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                background: "linear-gradient(135deg, #004f54, #007a85, #007a85)",
                color: "white",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                p: 4,
                width: "55%"
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 2 }}>
                <img
                  src={ayujalogo}
                  alt="Ayuja Logo"
                  style={{
                    height: "80px",
                    width: "auto",
                    display: "block",
                  }}
                />
              </Box>

              <Typography
                variant="h4"
                sx={{ fontWeight: "bold", mb: 2, textAlign: "center" }}
              >
                Welcome <br />
                Back to Ayuja
              </Typography>

              <Typography
                variant="h6"
                sx={{ mb: 2, textAlign: "center" }}
              >
                Your trusted partner in personalized in-home care for every stage of life
              </Typography>

              {/* Features */}
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 3, mb: 4 }}>
                <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                   24/7 Support
                </Typography>
                <br />
                <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                   Certified Caregivers
                </Typography>
              </Box>

              {/* Emergency Support Box */}
              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "12px",
                  p: 3,
                  textAlign: "center",
                  width: "50%",
                  marginLeft: "100px"
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  Emergency Support Available
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Need immediate assistance? Our emergency support team is available 24/7 to help you
                  and your loved ones.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate("/contactus")}
                  sx={{
                    backgroundColor: "#80ED99",
                    "&:hover": { backgroundColor: "#80ED99" },
                    color: "#22577A",
                    textTransform: "none",
                    borderRadius: "8px",
                  }}
                >
                  Contact Emergency
                </Button>
              </Box>
            </Grid>

            {/* Right Section */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                backgroundColor: "white",
                p: 4,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography variant="h5" fontWeight="bold" mb={1}>
                Sign In
              </Typography>
              <Typography variant="body2" mb={3} color="text.secondary">
                Access your personalized care dashboard
              </Typography>

              <TextField
                label="Email or Phone"
                name="identifier"
                value={formData.identifier}
                onChange={handleChange}
                placeholder="Enter email or 10-digit phone"
                fullWidth
                margin="normal"
              />

              <TextField
                label="Password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                type={showPassword ? "text" : "password"}
                fullWidth
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <FormControlLabel control={<Checkbox />} label="Remember me" />
                <Button
                  variant="text"
                  sx={{ textTransform: "none" }}
                  onClick={() => navigate("/ForgotPassword")}
                >
                  Forgot Password?
                </Button>
              </Box>

              <Button
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: "#007a85",
                  "&:hover": { backgroundColor: "#005f64" },
                  mb: 3,
                }}
                onClick={handleLogin}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Sign In"}
              </Button>

              <Typography textAlign="center" mb={2}>
                Don't have an account?{" "}
                <a href="/Register" style={{ color: "#007a85", fontWeight: "bold" }}>
                  Sign Up
                </a>
              </Typography>
            </Grid>
          </Grid>
        </Card>

        {/*  Custom Dialog for all messages */}
        <CustomDialog
          open={dialog.open}
          title={dialog.title}
          message={dialog.message}
          type={dialog.type}
          onClose={() => setDialog({ ...dialog, open: false })}
        />
      </Box>

      <Footer />
    </>
  );
};

export default Login;